import React, { useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import { ProtectedRoute } from './components/ProtectedRoute';
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { ToastContainer } from 'react-toastify';
import Landing from './components/Landing';
import Login, { LoginBase } from './components/Login';
import BusquedaGlobalResultados from './components/BusquedaGlobalResultados';
import store from './actions/store';
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import Auth from './auth';
import MiniSpinner from './components/General/MiniSpinner';
import { loadFrontendConfig, setFrontendConfig } from './helper/frontendConfigHelper';

const applyFrontendFeatures = (features) => {
    if (typeof window === 'undefined') return;
    if (features && typeof features === 'object') {
        window.__FRONTEND_FEATURES__ = features;
        return;
    }
    window.__FRONTEND_FEATURES__ = {};
};

function App() {
    const [isLoading, setIsLoading] = useState(true);
    const [frontendConfigLoaded, setFrontendConfigLoaded] = useState(false);
    const [recaptchaKey, setRecaptchaKey] = useState(process.env.REACT_APP_SITE_KEY || '');
    const [recaptchaError, setRecaptchaError] = useState(false);

    useEffect(() => {
        // Busca sesion existente
        Auth.checkSession();
        setIsLoading(false);

        // Busca actividad para establecer estado inactivo
        const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
        const updateActivity = () => Auth.updateActivity();

        events.forEach(event => {
            window.addEventListener(event, updateActivity);
        });

        // Revisa periodicamente la sesion
        const intervalId = setInterval(() => {
            Auth.checkSession();
        }, 60 * 1000); // Cada minuto

        return () => {
            events.forEach(event => {
                window.removeEventListener(event, updateActivity);
            });
            clearInterval(intervalId);
        };
    }, []);

    useEffect(() => {
        let cancelled = false;

        const hydrateFrontendConfig = async () => {
            const envSiteKey = process.env.REACT_APP_SITE_KEY || '';
            const envSecret = process.env.REACT_APP_AUTH_SHARED_SECRET || '';
            const winSecret = (typeof window !== 'undefined' && window.__AUTH_SHARED_SECRET__)
                ? window.__AUTH_SHARED_SECRET__
                : '';
            const winEnvironment = (typeof window !== 'undefined' && window.__APP_ENVIRONMENT__)
                ? window.__APP_ENVIRONMENT__
                : '';

            setFrontendConfig({
                recaptchaSiteKey: envSiteKey,
                authSharedSecret: winSecret || envSecret,
                environmentName: winEnvironment
            });

            let config = {};
            try {
                config = await loadFrontendConfig();
            } catch {
                config = setFrontendConfig({});
            }

            try {
                if (cancelled) return;

                applyFrontendFeatures(config?.frontendFeatures);

                const resolvedRecaptchaKey = (envSiteKey || config?.recaptchaSiteKey || '').trim();
                const resolvedSharedSecret = (winSecret || envSecret || config?.authSharedSecret || '').trim();
                const resolvedEnvironmentName = (winEnvironment || config?.environmentName || '').trim();

                if (resolvedRecaptchaKey) {
                    setRecaptchaKey(resolvedRecaptchaKey);
                    setRecaptchaError(false);
                } else {
                    setRecaptchaError(true);
                }

                if (typeof window !== 'undefined') {
                    if (resolvedSharedSecret) {
                        window.__AUTH_SHARED_SECRET__ = resolvedSharedSecret;
                    }

                    if (resolvedEnvironmentName) {
                        window.__APP_ENVIRONMENT__ = resolvedEnvironmentName;
                    }
                }
            } finally {
                if (!cancelled) {
                    setFrontendConfigLoaded(true);
                }
            }
        };

        hydrateFrontendConfig();
        return () => { cancelled = true; };
    }, []);

    if (isLoading || !frontendConfigLoaded) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <MiniSpinner />
            </div>
        );
    }

    const AppRoutes = (
        <BrowserRouter>
            <Routes>
                <Route path="/login" element={recaptchaKey ? <Login /> : <LoginBase />} />
                <Route path="/busqueda-global-resultados" element={<ProtectedRoute><BusquedaGlobalResultados /></ProtectedRoute>} />
                <Route path="/*" element={<ProtectedRoute><Landing /></ProtectedRoute>} />
            </Routes>
            <ToastContainer
                position="top-right"
                autoClose={4500}
                hideProgressBar={false}
                newestOnTop
                closeOnClick={false}
                rtl={false}
                pauseOnFocusLoss
                draggable={false}
                pauseOnHover
                limit={5}
                theme="light"
                toastClassName="app-toast"
            />
        </BrowserRouter>
    );

    return (
        <Provider store={store}>
            {recaptchaKey ? (
                <GoogleReCaptchaProvider reCaptchaKey={recaptchaKey} scriptProps={{ appendTo: "body" }}>
                    {AppRoutes}
                </GoogleReCaptchaProvider>
            ) : (
                <>
                    {recaptchaError ? (
                        <div className="p-2" style={{ background: '#fff3cd', color: '#856404' }}>
                            reCAPTCHA no configurado (SiteKey vacía). Revisar `Recaptcha:SiteKey` en appsettings o `REACT_APP_SITE_KEY`.
                        </div>
                    ) : null}
                    {AppRoutes}
                </>
            )}
        </Provider>
    );
}

export default App;
